/**
 * TemplatesEditor — "Templates" sub-tab.
 * List / create / edit / delete Fax cover-sheet templates that pre-fill
 * the SendPanel's subject/message.
 *
 * Endpoints:
 *   GET    /api/fax/templates
 *   POST   /api/fax/templates
 *   PUT    /api/fax/templates/{id}
 *   DELETE /api/fax/templates/{id}
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Loader2, X } from "lucide-react";
import { faxAPI } from "./faxAPI";
import { NAVY, GOLD, GREEN, RED, NAVY_HAIRLINE, NAVY_TINT } from "../tokens";

function EditForm({ initial, onCancel, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [subject, setSubject] = useState(initial?.subject || "");
  // Backend field is `content`; older builds sometimes returned `message`.
  // Read both, write `content` (backend contract).
  const [message, setMessage] = useState(initial?.content || initial?.message || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Template needs a name");
    setSaving(true);
    try {
      // Backend requires `content`, not `message`. Send `subject` too so
      // the cover-sheet subject persists — BE doesn't currently store it
      // but this future-proofs the payload.
      const payload = { name: name.trim(), subject, content: message };
      if (initial?.id) {
        await faxAPI.updateTemplate(initial.id, payload);
        toast.success("Template updated");
      } else {
        await faxAPI.createTemplate(payload);
        toast.success("Template created");
      }
      onSaved && onSaved();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg bg-white border p-3 space-y-2" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-fax-tpl-form">
      <div className="flex items-center gap-2">
        <div className="text-[13px] font-semibold flex-1" style={{ color: NAVY }}>
          {initial?.id ? "Edit template" : "New template"}
        </div>
        <button onClick={onCancel} className="w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Template name (e.g. Referral to PCP)"
        className="w-full h-8 px-2 rounded-md border text-[12.5px] outline-none"
        style={{ borderColor: NAVY_HAIRLINE }}
        data-testid="comms2-fax-tpl-name"
      />
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Cover-sheet subject"
        className="w-full h-8 px-2 rounded-md border text-[12.5px] outline-none"
        style={{ borderColor: NAVY_HAIRLINE }}
        data-testid="comms2-fax-tpl-subject"
      />
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={4}
        placeholder="Cover-sheet note"
        className="w-full px-2 py-1.5 rounded-md border text-[12.5px] outline-none resize-none"
        style={{ borderColor: NAVY_HAIRLINE }}
        data-testid="comms2-fax-tpl-message"
      />
      <div className="flex items-center gap-2">
        <button
          onClick={submit}
          disabled={saving}
          className="h-9 px-4 rounded-md text-[12.5px] font-bold flex items-center gap-1.5 disabled:opacity-40"
          style={{ background: GOLD, color: NAVY }}
          data-testid="comms2-fax-tpl-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
        <button onClick={onCancel} className="h-9 px-3 rounded-md text-[12.5px] font-medium hover:bg-slate-100" data-testid="comms2-fax-tpl-cancel">Cancel</button>
      </div>
    </div>
  );
}

export default function TemplatesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | {id, ...}
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await faxAPI.templates();
      setItems(Array.isArray(r) ? r : (r?.items || []));
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    if (!window.confirm(`Delete template “${t.name}”?`)) return;
    setBusyId(t.id);
    try { await faxAPI.deleteTemplate(t.id); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(e.message || "Delete failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="p-4 space-y-3">
      {!editing && (
        <div className="flex items-center gap-2">
          <div className="text-[12.5px] text-slate-600">
            {loading ? "Loading templates…" : items.length === 0 ? "No templates yet — save your first one below." : `${items.length} template${items.length === 1 ? "" : "s"}.`}
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setEditing({})}
            className="h-9 px-3 rounded-md text-[12.5px] font-bold flex items-center gap-1.5"
            style={{ background: GOLD, color: NAVY }}
            data-testid="comms2-fax-tpl-new"
          >
            <Plus className="w-3.5 h-3.5" /> New template
          </button>
        </div>
      )}
      {editing && <EditForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {items.length > 0 && !editing && (
        <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: NAVY_HAIRLINE }}>
          {items.map(t => (
            <div key={t.id} className="flex items-center gap-3 px-3 py-2 border-b last:border-0" style={{ borderColor: NAVY_HAIRLINE }} data-testid={`comms2-fax-tpl-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold truncate" style={{ color: NAVY }}>{t.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {t.subject || "(no subject)"} · {(t.content || t.message) ? (t.content || t.message).slice(0, 80) + ((t.content || t.message).length > 80 ? "…" : "") : "(no note)"}
                </div>
              </div>
              <button
                onClick={() => setEditing(t)}
                className="h-8 px-3 rounded-md border text-[11.5px] font-medium hover:bg-slate-50"
                style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                data-testid={`comms2-fax-tpl-edit-${t.id}`}
              >
                Edit
              </button>
              <button
                onClick={() => del(t)}
                disabled={busyId === t.id}
                className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center disabled:opacity-40"
                title="Delete"
                data-testid={`comms2-fax-tpl-del-${t.id}`}
              >
                {busyId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" style={{ color: RED }} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
